import React, { useEffect, useState, useRef } from "react";
import WaveSurfer from "wavesurfer.js";
import * as Sentry from "@sentry/react"
// a cache to store waveform instances across component re-renders
const waveformCache = new Map();

// Helper function for debounced waveform sync
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const WaveformPlayer = ({
  audioSrc,
  currentTime,
  duration,
  isPlaying,
  onSeek,
  isActive,
}) => {
  // Log received props
  console.log("[WaveformPlayer] Received props:", { audioSrc, isPlaying, isActive, currentTime });

  const waveformRef = useRef(null);
  const waveSurferRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [localDuration, setLocalDuration] = useState(duration || 0);
  const [cacheKey, setCacheKey] = useState('');
  
  // Add debounced tracking for currentTime to reduce update frequency and improve sync
  const debouncedTime = useDebounce(currentTime, 50); // 50ms debounce
  useEffect(() => {
    if (!waveformRef?.current || !audioSrc) return;

    // Create a unique key for this audio source
    const key = `waveform-${audioSrc}`;
    setCacheKey(key);
      // Check if we already have this waveform in the cache
    if (waveformCache.has(key)) {
      console.log("[WaveformPlayer] Using cached waveform for:", audioSrc);
      waveSurferRef.current = waveformCache.get(key);
      setIsLoading(false);
      setHasError(false);
      
      // Update reference to the container by destroying and recreating
      if (waveSurferRef.current) {
        // Store the current settings
        const oldWs = waveSurferRef.current;
        
        // Create new instance with the current container
        const newWs = WaveSurfer.create({
          container: waveformRef.current,
          waveColor: "rgba(255, 255, 255, 0.15)",
          progressColor: "#ffff",
          cursorWidth: 0,
          responsive: true,
          interact: false,
          barWidth: 2,
          barGap: 2,
          height: 36,
          barRadius: 2,
          normalize: true,
        });
        
        // Load the same audio
        newWs.load(audioSrc);
        newWs.setVolume(0);
        
        // Update the cache and reference
        waveSurferRef.current = newWs;
        waveformCache.set(key, newWs);
        
        // Clean up the old instance
        oldWs.destroy();
        
        // Always start from beginning when a track changes
        newWs.seekTo(0);
      }
      return;
    }

    console.log("[WaveformPlayer] Initializing new WaveSurfer with URL:", audioSrc);
    setIsLoading(true);
    setHasError(false);

    // Initialize WaveSurfer similar to AdvancedEditAudioPlayer
    const ws = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: "rgba(255, 255, 255, 0.15)",
      progressColor: "#ffff",
      cursorWidth: 0,
      responsive: true,
      interact: false, // will not allow seeking on the waveform
      barWidth: 2,
      barGap: 2,
      height: 36,
      barRadius: 2,
      normalize: true,
    });    // Set up event handlers
    ws.on('ready', () => {
      console.log("[WaveformPlayer] Audio loaded successfully:", audioSrc);
      setIsLoading(false);
      setHasError(false);
      const audioDuration = ws.getDuration();
      setLocalDuration(audioDuration);
      console.log("[WaveformPlayer] Audio duration:", audioDuration);
      
      // Store in cache for future use
      waveformCache.set(key, ws);
      
      // Always start new tracks from the beginning (position 0)
      ws.seekTo(0);
    });
    
    ws.on('error', (err) => {
      console.error("[WaveformPlayer] WaveSurfer error:", err);
      setIsLoading(false);
      setHasError(true);
    });
      // Load the audio file
    try {
      // Add specific event handler for the initial load to reset position
      const initialLoadHandler = () => {
        console.log("[WaveformPlayer] Initial load event - ensuring position is 0");
        if (ws && ws.seekTo) {
          ws.seekTo(0);  // Force position to 0 at load
        }
      };
      
      ws.on('loading', initialLoadHandler);
      ws.load(audioSrc);
      ws.setVolume(0); // Mute the audio since we're just showing the waveform
      waveSurferRef.current = ws;
    } catch (error) {
      console.error("[WaveformPlayer] Error loading audio:", error);
      Sentry.captureException(error);
      setIsLoading(false);
      setHasError(true);
    }

    // Only clean up entries from the cache when component unmounts entirely,
    // not when switching between songs
    return () => {
      // We intentionally don't destroy the wavesurfer instances here
      // to keep them in cache
    };
  }, [audioSrc]);  // Track previous audio source to detect changes
  const prevAudioSrcRef = useRef(null);
  
  // Sync waveform progress with the current time - maintain highlights when paused
  useEffect(() => {
    if (waveSurferRef.current && !isLoading && !hasError) {
      const durationToUse = localDuration || duration;
      
      // Detect if audio source has changed
      const audioSourceChanged = prevAudioSrcRef.current !== audioSrc;
      if (audioSourceChanged) {
        console.log(`[WaveformPlayer] Audio source changed - forcing waveform to start at 0`);
        prevAudioSrcRef.current = audioSrc;
        waveSurferRef.current.seekTo(0);
        return;
      }
      
      // Update the waveform if this track is active (keep highlight even when paused)
      if (isActive) {
        if (durationToUse > 0 && currentTime >= 0) {
          // Catch the case when a track first starts playing (should always start from 0)
          if (currentTime < 0.1) {
            console.log(`[WaveformPlayer] Near beginning state detected - forcing waveform to start at 0`);
            waveSurferRef.current.seekTo(0);
          } else {
            // Normal progress tracking during playback
            const progress = Math.min(currentTime / durationToUse, 1);
            console.log(`[WaveformPlayer] Syncing waveform to currentTime: ${currentTime}s, progress: ${progress}`);
            waveSurferRef.current.seekTo(progress);
          }
        } else {
          // If time or duration is invalid, reset to beginning
          console.log(`[WaveformPlayer] Invalid time/duration - resetting to 0`);
          waveSurferRef.current.seekTo(0);
        }
      } else {
        // Reset progress for inactive songs
        console.log(`[WaveformPlayer] Track inactive - resetting to 0`);
        waveSurferRef.current.seekTo(0);
      }
    }
  }, [currentTime, duration, localDuration, isActive, isLoading, hasError, isPlaying, audioSrc]);
  // Clean up the cache when the component is unmounted entirely
  useEffect(() => {
    return () => {
      if (cacheKey && waveformCache.has(cacheKey)) {
        const ws = waveformCache.get(cacheKey);
        if (ws) {
          ws.destroy();
          waveformCache.delete(cacheKey);
        }
      }
    };
  }, [cacheKey]);

  // Global cleanup function (when app unmounts)
  useEffect(() => {
    return () => {
      // Clear the entire cache on component unmount
      waveformCache.forEach(ws => {
        if (ws) ws.destroy();
      });
      waveformCache.clear();
    };
  }, []);  return (
    <div style={{ width: "280px", height: "100%", position: "relative" }}>      
    {isLoading && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
        </div>
      )}
      <div 
        ref={waveformRef} 
        style={{ width: "100%", height: "36px", visibility: isLoading ? "hidden" : "visible" }}
        className={hasError ? "waveform-error" : ""}
      />
    </div>
  );
};

export default WaveformPlayer;